<template>
  <el-drawer
    :model-value="internalOpen"
    direction="btt"
    size="430px"
    append-to-body
    title="选择卡片主题"
    :with-header="false"
    :show-close="false"
    :modal="true"
    modal-class="data-center-theme-overlay"
    :close-on-click-modal="true"
    :close-on-press-escape="true"
    :lock-scroll="false"
    trap-focus
    class="data-center-theme-drawer"
    @update:model-value="setInternalOpen"
    @opened="focusSelectedTheme"
  >
    <section class="theme-panel" aria-labelledby="data-center-theme-title">
      <header class="theme-panel__header">
        <h2 id="data-center-theme-title">选择卡片主题</h2>
        <button
          class="theme-panel__close"
          type="button"
          aria-label="关闭主题选择"
          @click="setInternalOpen(false)"
        >
          <el-icon><Close /></el-icon>
        </button>
      </header>

      <div class="theme-panel__options" role="radiogroup" aria-label="数据中心卡片主题">
        <button
          v-for="theme in themes"
          :key="theme.id"
          ref="themeButtons"
          class="theme-option"
          :class="{ 'is-selected': theme.id === selectedThemeId }"
          type="button"
          role="radio"
          :aria-checked="theme.id === selectedThemeId"
          :data-theme-id="theme.id"
          @click="selectTheme(theme.id)"
        >
          <span class="theme-option__preview" :style="getDataCenterThemeStyle(theme)">
            <img :src="theme.asset" alt="" aria-hidden="true" />
          </span>
          <span class="theme-option__label">{{ theme.label }}</span>
          <span v-if="theme.id === selectedThemeId" class="theme-option__check" aria-hidden="true">✓</span>
        </button>
      </div>
    </section>
  </el-drawer>
</template>

<script setup>
import { nextTick, onUnmounted, ref, toRef } from 'vue';
import { Close } from '@element-plus/icons-vue';
import { getDataCenterThemeStyle } from '@/config/dataCenterThemes.mjs';
import { createBodyOverflowLock } from '@/utils/dataCenterThemeDrawerScrollLock.mjs';
import { createDataCenterThemeDrawerOpenState } from '@/utils/dataCenterThemeDrawerOpenState.mjs';

const props = defineProps({
  open: { type: Boolean, required: true },
  themes: { type: Array, required: true },
  selectedThemeId: { type: String, required: true },
});

const emit = defineEmits(['update:open', 'select']);
const themeButtons = ref([]);
const scrollLock = createBodyOverflowLock();
const drawerState = createDataCenterThemeDrawerOpenState(toRef(props, 'open'), emit, scrollLock);
const { internalOpen, setInternalOpen } = drawerState;

onUnmounted(() => drawerState.dispose());

function selectTheme(themeId) {
  emit('select', themeId);
}

async function focusSelectedTheme() {
  await nextTick();
  const selectedButton = themeButtons.value.find(
    (button) => button?.dataset?.themeId === props.selectedThemeId,
  );
  selectedButton?.focus();
}
</script>

<style scoped>
:global(.data-center-theme-overlay) {
  --el-transition-duration: 0.2s;
  background: transparent !important;
}

:global(.data-center-theme-drawer) {
  border-radius: 18px 18px 0 0;
  background: #ffffff;
  box-shadow: 0 -16px 40px rgba(34, 77, 121, 0.12), 0 -2px 8px rgba(34, 77, 121, 0.06);
}

:global(.data-center-theme-drawer .el-drawer__body) {
  padding: 0;
}

.theme-panel {
  box-sizing: border-box;
  width: min(1380px, 100%);
  height: 100%;
  margin: 0 auto;
  padding: 20px 28px 22px;
}

.theme-panel__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  min-height: 36px;
  margin-bottom: 16px;
  border-bottom: 1px solid rgba(226, 232, 240, 0.78);
  padding-bottom: 12px;
}

.theme-panel__header h2 {
  margin: 0;
  color: #1f3b5b;
  font-size: 17px;
  font-weight: 700;
}

.theme-panel__close {
  display: inline-grid;
  width: 34px;
  height: 34px;
  place-items: center;
  border: 0;
  border-radius: 50%;
  background: transparent;
  color: #51677f;
  cursor: pointer;
}

.theme-panel__close:hover,
.theme-panel__close:focus-visible {
  background: #f1f7fc;
  color: #1499dc;
  outline: none;
}

.theme-panel__options {
  display: grid;
  grid-template-columns: repeat(6, minmax(0, 1fr));
  grid-template-rows: repeat(2, minmax(0, 1fr));
  gap: 10px 12px;
}

.theme-option {
  position: relative;
  box-sizing: border-box;
  min-width: 0;
  padding: 5px 5px 7px;
  border: 2px solid transparent;
  border-radius: 14px;
  background: #ffffff;
  color: #42566d;
  cursor: pointer;
  transition: border-color 0.18s ease, box-shadow 0.18s ease, transform 0.18s ease;
}

.theme-option:hover {
  transform: translateY(-2px);
  border-color: rgba(112, 204, 242, 0.55);
}

.theme-option:focus-visible {
  outline: 2px solid rgba(34, 183, 237, 0.72);
  outline-offset: 2px;
}

.theme-option.is-selected {
  border-color: #22b7ed;
  box-shadow: 0 0 0 3px rgba(34, 183, 237, 0.12), 0 8px 20px rgba(44, 143, 190, 0.12);
}

.theme-option__preview {
  position: relative;
  display: block;
  width: min(176px, 100%);
  margin: 0 auto;
  aspect-ratio: 11 / 7;
  overflow: hidden;
  border: 1px solid rgba(220, 232, 245, 0.55);
  border-radius: 11px;
  background:
    radial-gradient(circle at 82% 82%, var(--dc-theme-glow) 0%, var(--dc-theme-soft-glow) 38%, rgba(255, 255, 255, 0) 68%),
    linear-gradient(145deg, #ffffff 0%, #fdfefe 68%, var(--dc-theme-tail) 100%);
}

.theme-option__preview img {
  position: absolute;
  right: var(--dc-artwork-right);
  bottom: var(--dc-artwork-bottom);
  width: var(--dc-artwork-width);
  max-width: none;
  transform: scaleX(var(--dc-artwork-scale-x));
  transform-origin: right bottom;
  filter: var(--dc-artwork-filter);
  pointer-events: none;
}

.theme-option__label {
  display: block;
  margin-top: 7px;
  overflow: hidden;
  font-size: 13px;
  font-weight: 600;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.theme-option__check {
  position: absolute;
  top: 10px;
  right: 10px;
  z-index: 3;
  display: grid;
  width: 20px;
  height: 20px;
  place-items: center;
  border-radius: 50%;
  background: #22b7ed;
  color: #ffffff;
  font-size: 12px;
  font-weight: 800;
  box-shadow: 0 3px 8px rgba(34, 183, 237, 0.25);
}

@media (max-width: 900px) {
  .theme-panel { padding: 16px 14px 18px; }

  .theme-panel__options {
    grid-template-columns: repeat(6, 190px);
    grid-template-rows: repeat(2, minmax(0, 1fr));
    gap: 10px;
    overflow-x: auto;
    padding: 2px 2px 10px;
    scroll-snap-type: x proximity;
  }

  .theme-option { scroll-snap-align: start; }
}

@media (prefers-reduced-motion: reduce) {
  :global(.data-center-theme-drawer),
  :global(.data-center-theme-overlay) {
    --el-transition-duration: 0ms;
    animation: none !important;
    transition: none !important;
  }

  .theme-option {
    transition: none;
  }

  .theme-option:hover {
    transform: none;
  }
}
</style>
